import { useEffect, useState } from 'react'
import {
  Title, Group, Card, Stack, Text, TextInput, Textarea, Button,
  Table, Badge, ActionIcon, Modal, LoadingOverlay, NumberInput, Tooltip, ColorInput,
  MultiSelect,
} from '@mantine/core'
import {
  IconPlus, IconPencil, IconTrash, IconEye, IconEyeOff, IconPhoto,
  IconArrowUp, IconArrowDown, IconDeviceFloppy, IconX,
} from '@tabler/icons-react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { confirmDelete, confirmAction } from '@/lib/confirm'
import { useBranches, useServices } from '@/lib/swr'
import type { Branch, Service } from '@/types'
import MapPicker from '@/components/MapPicker'

interface BranchFormData {
  name: string
  badge: string
  badgeColor: string
  image: string
  description: string
  address: string
  email: string
  hours: string
  phones: { value: string }[]
  services: string[]
  highlights: { value: string; label: string }[]
  mapSrc: string
  lat: number
  lng: number
  zoom: number
  order: number
}

const empty: BranchFormData = {
  name: '', badge: '', badgeColor: '#006C67', image: '', description: '',
  address: '', email: '', hours: '', phones: [], services: [], highlights: [],
  mapSrc: '', lat: 0, lng: 0, zoom: 15, order: 0,
}

function pickFile(onFile: (file: File) => void) {
  const inp = document.createElement('input')
  inp.type = 'file'
  inp.accept = 'image/*'
  inp.onchange = () => inp.files?.[0] && onFile(inp.files[0])
  inp.click()
}

export default function BranchesPage() {
  const { data, isLoading, mutate } = useBranches()
  const { data: servicesData } = useServices({ limit: '200' })
  const serviceOptions = ((servicesData?.data ?? []) as Service[]).map((s) => ({ value: s._id, label: s.name }))
  const [items, setItems] = useState<Branch[]>([])
  const [orderChanged, setOrderChanged] = useState(false)
  const [reordering, setReordering] = useState(false)

  useEffect(() => {
    const list: Branch[] = data?.data ?? []
    setItems([...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
    setOrderChanged(false)
  }, [data])

  const moveUp = (index: number) => {
    if (index === 0) return
    setItems((prev) => { const next = [...prev]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return next })
    setOrderChanged(true)
  }
  const moveDown = (index: number) => {
    if (index === items.length - 1) return
    setItems((prev) => { const next = [...prev]; [next[index], next[index + 1]] = [next[index + 1], next[index]]; return next })
    setOrderChanged(true)
  }
  const saveOrder = () => confirmAction('Шинэ дарааллыг хадгалах уу?', async () => {
    setReordering(true)
    try {
      await api.admin.reorderBranches(items.map((i) => i._id))
      toast.success('Дараалал хадгалагдлаа')
      setOrderChanged(false); mutate()
    } catch (e: any) {
      toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа')
    } finally { setReordering(false) }
  }, { title: 'Дараалал хадгалах', confirmLabel: 'Хадгалах' })

  const [open, setOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  const { control, handleSubmit, reset, setValue, watch, formState: { errors, isSubmitting } } =
    useForm<BranchFormData>({ defaultValues: empty })
  const watchImage = watch('image')

  const phonesArr = useFieldArray({ control, name: 'phones' })
  const highlightsArr = useFieldArray({ control, name: 'highlights' })

  const create = () => { setEditId(null); reset(empty); setOpen(true) }
  const edit = (b: Branch) => {
    setEditId(b._id)
    reset({
      name: b.name, badge: b.badge, badgeColor: b.badgeColor || '#006C67',
      image: b.image, description: b.description, address: b.address,
      email: b.email, hours: b.hours, mapSrc: b.mapSrc || '', order: b.order,
      lat: b.lat ?? 0, lng: b.lng ?? 0, zoom: b.zoom ?? 15,
      phones: (b.phones || []).map((v) => ({ value: v })),
      services: (b.services || []).map((s) => typeof s === 'object' ? s._id : s),
      highlights: (b.highlights || []).map((h) => ({ value: h.value, label: h.label })),
    })
    setOpen(true)
  }
  const close = () => { setOpen(false); reset(empty) }

  const onSubmit = handleSubmit(async (form) => {
    try {
      const payload = {
        name: form.name,
        badge: form.badge,
        badgeColor: form.badgeColor,
        image: form.image,
        description: form.description,
        address: form.address,
        email: form.email,
        hours: form.hours,
        mapSrc: '',
        lat: form.lat,
        lng: form.lng,
        zoom: form.zoom,
        order: form.order,
        phones: form.phones.map((p) => p.value).filter(Boolean),
        services: form.services,
        highlights: form.highlights.filter((h) => h.value || h.label),
      }
      if (editId) await api.admin.updateBranch(editId, payload)
      else await api.admin.createBranch(payload)
      toast.success(editId ? 'Шинэчлэгдлээ' : 'Нэмэгдлээ')
      close(); mutate()
    } catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  })

  const remove = (id: string) => confirmDelete('Энэ салбарыг устгах уу?', async () => {
    try { await api.admin.deleteBranch(id); mutate() }
    catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  })
  const toggle = async (b: Branch) => {
    try { await api.admin.updateBranchStatus(b._id, !b.isActive); mutate() }
    catch (e: any) { toast.error(e instanceof Error ? e.message : 'Алдаа гарлаа') }
  }
  const upload = async (file: File) => {
    setUploading(true)
    try { const r = await api.admin.uploadImage(file); setValue('image', r.url, { shouldDirty: true }) }
    catch { toast.error('Зураг алдаа') }
    finally { setUploading(false) }
  }

  return (
    <Stack>
      <Title order={2}>Салбарууд</Title>
      <Card withBorder>
        <Stack>
          <Group justify="space-between">
            <Text fw={500}>Салбарууд ({items.length})</Text>
            <Group gap="xs">
              {orderChanged && (
                <Button size="xs" variant="light" color="orange" leftSection={<IconDeviceFloppy size={14} />} onClick={saveOrder} loading={reordering}>Дараалал хадгалах</Button>
              )}
              <Button size="xs" leftSection={<IconPlus size={14} />} onClick={create}>Нэмэх</Button>
            </Group>
          </Group>
          <div style={{ position: 'relative' }}>
            <LoadingOverlay visible={isLoading} />
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Зураг</Table.Th>
                  <Table.Th>Нэр</Table.Th>
                  <Table.Th>Хаяг</Table.Th>
                  <Table.Th>Статус</Table.Th>
                  <Table.Th w={170}>Үйлдэл</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((b, idx) => (
                  <Table.Tr key={b._id}>
                    <Table.Td>{b.image ? <img src={b.image} alt="" style={{ width: 48, height: 36, borderRadius: 4, objectFit: 'cover' }} /> : '—'}</Table.Td>
                    <Table.Td><Text size="sm" fw={600}>{b.name}</Text><Text size="xs" c="dimmed">{b.badge}</Text></Table.Td>
                    <Table.Td><Text size="xs">{b.address}</Text></Table.Td>
                    <Table.Td><Badge color={b.isActive ? 'green' : 'gray'} variant="light" size="sm">{b.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}</Badge></Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Tooltip label="Дээш" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === 0} onClick={() => moveUp(idx)}><IconArrowUp size={14} /></ActionIcon></Tooltip>
                        <Tooltip label="Доош" position="top" withArrow><ActionIcon variant="subtle" color="gray" size="sm" disabled={idx === items.length - 1} onClick={() => moveDown(idx)}><IconArrowDown size={14} /></ActionIcon></Tooltip>
                        <ActionIcon variant="light" size="sm" onClick={() => toggle(b)}>{b.isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}</ActionIcon>
                        <ActionIcon variant="light" size="sm" onClick={() => edit(b)}><IconPencil size={14} /></ActionIcon>
                        <ActionIcon variant="light" color="red" size="sm" onClick={() => remove(b._id)}><IconTrash size={14} /></ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {items.length === 0 && !isLoading && (
                  <Table.Tr><Table.Td colSpan={5}><Text ta="center" c="dimmed" py="lg">Хоосон</Text></Table.Td></Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </div>
        </Stack>
      </Card>

      <Modal opened={open} onClose={close} title={editId ? 'Салбар засах' : 'Шинэ салбар'} size="xl">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller name="name" control={control} rules={{ validate: (v) => v.trim() ? true : 'Нэр шаардлагатай' }} render={({ field }) => <TextInput label="Нэр *" {...field} error={errors.name?.message} />} />
            <Group grow>
              <Controller name="badge" control={control} render={({ field }) => <TextInput label="Тэмдэг (badge)" {...field} />} />
              <Controller name="badgeColor" control={control} render={({ field }) => <ColorInput label="Тэмдгийн өнгө" {...field} />} />
            </Group>
            <Controller name="description" control={control} render={({ field }) => <Textarea label="Тайлбар" {...field} minRows={3} />} />
            <Controller name="address" control={control} render={({ field }) => <Textarea label="Хаяг" {...field} minRows={2} />} />
            <Controller name="email" control={control} render={({ field }) => <TextInput label="И-мэйл" {...field} />} />
            <Controller name="hours" control={control} render={({ field }) => <Textarea label="Ажиллах цаг (мөр бүр шинэ цаг)" {...field} minRows={3} />} />

            {/* Phones */}
            <div>
              <Text size="sm" fw={500} mb={4}>Утаснууд</Text>
              <Stack gap={4}>
                {phonesArr.fields.map((f, i) => (
                  <Group key={f.id} gap={4}>
                    <Controller name={`phones.${i}.value`} control={control} render={({ field }) => <TextInput flex={1} placeholder="Утас" {...field} />} />
                    <ActionIcon variant="light" color="red" onClick={() => phonesArr.remove(i)}><IconX size={14} /></ActionIcon>
                  </Group>
                ))}
                <Button size="xs" variant="light" leftSection={<IconPlus size={12} />} onClick={() => phonesArr.append({ value: '' })} style={{ alignSelf: 'flex-start' }}>Утас нэмэх</Button>
              </Stack>
            </div>

            {/* Services */}
            <Controller
              name="services"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  label="Үйлчилгээ"
                  placeholder="Үйлчилгээ сонгох"
                  data={serviceOptions}
                  value={field.value}
                  onChange={field.onChange}
                  searchable
                  clearable
                />
              )}
            />

            {/* Highlights */}
            <div>
              <Text size="sm" fw={500} mb={4}>Highlights</Text>
              <Stack gap={4}>
                {highlightsArr.fields.map((f, i) => (
                  <Group key={f.id} gap={4}>
                    <Controller name={`highlights.${i}.value`} control={control} render={({ field }) => <TextInput placeholder="Утга (e.g. 80)" {...field} />} />
                    <Controller name={`highlights.${i}.label`} control={control} render={({ field }) => <TextInput flex={1} placeholder="Шошго (e.g. Ор)" {...field} />} />
                    <ActionIcon variant="light" color="red" onClick={() => highlightsArr.remove(i)}><IconX size={14} /></ActionIcon>
                  </Group>
                ))}
                <Button size="xs" variant="light" leftSection={<IconPlus size={12} />} onClick={() => highlightsArr.append({ value: '', label: '' })} style={{ alignSelf: 'flex-start' }}>Highlight нэмэх</Button>
              </Stack>
            </div>

            <div>
              <Text size="sm" fw={500} mb={4}>Байршил (газрын зураг дээр дарж сонгоно)</Text>
              <MapPicker
                lat={watch('lat') || 0}
                lng={watch('lng') || 0}
                zoom={watch('zoom') || 15}
                onChange={({ lat, lng, zoom }) => {
                  setValue('lat', lat, { shouldDirty: true })
                  setValue('lng', lng, { shouldDirty: true })
                  setValue('zoom', zoom, { shouldDirty: true })
                }}
              />
            </div>
            <Group grow>
              <Controller name="lat" control={control} render={({ field }) => <NumberInput label="Latitude" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} decimalScale={6} step={0.0001} />} />
              <Controller name="lng" control={control} render={({ field }) => <NumberInput label="Longitude" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} decimalScale={6} step={0.0001} />} />
              <Controller name="zoom" control={control} render={({ field }) => <NumberInput label="Zoom" value={field.value} onChange={(v) => field.onChange(Number(v) || 15)} min={1} max={20} />} />
            </Group>
            <Controller name="order" control={control} render={({ field }) => <NumberInput label="Дараалал" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} />} />

            <div>
              <Text size="sm" fw={500} mb={4}>Зураг</Text>
              <Group>
                <Controller name="image" control={control} render={({ field }) => <TextInput flex={1} placeholder="URL" {...field} />} />
                <Button size="sm" variant="light" leftSection={<IconPhoto size={14} />} loading={uploading} onClick={() => pickFile(upload)}>Upload</Button>
              </Group>
              {watchImage && <img src={watchImage} alt="" style={{ marginTop: 8, maxHeight: 120, borderRadius: 8 }} />}
            </div>

            <Group justify="flex-end">
              <Button variant="light" onClick={close} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}
